// Guides written by the Assistant (and any step that sets `targetText`) point
// at a control by its visible label, e.g. "Nueva fuente", instead of a CSS
// selector: the model knows the words on screen, not our data-tour anchors.

/** Elements a "click here" instruction can reasonably point at. */
const CANDIDATES =
  'button, a, [role="tab"], [role="button"], [role="menuitem"], [role="option"], label, h1, h2, h3';

/** Leaf elements that carry one line of a control's text. */
const TEXT_LINES = 'span, div, p, strong, b, small';

/** Case-, accent- and whitespace-insensitive form of a label. */
export function normalizeLabel(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function labelOf(el: HTMLElement): string {
  // innerText skips hidden children; textContent covers environments without layout.
  return el.innerText || el.textContent || el.getAttribute('aria-label') || el.getAttribute('title') || '';
}

function isVisible(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/**
 * The visible control whose label matches `text`. An exact label wins; failing
 * that, the first short label that contains it ("Nueva fuente de datos" for
 * "Nueva fuente"); failing both, the control around a line that reads exactly
 * the label (a tile's name above its description). Anything inside
 * `[data-tour-skip]` (the tour card, the Assistant panel that wrote the guide)
 * is ignored so a step never spotlights its own instructions.
 */
export function findByText(text: string, root: ParentNode = document): HTMLElement | null {
  const want = normalizeLabel(text);
  if (!want) return null;
  let partial: HTMLElement | null = null;
  for (const el of Array.from(root.querySelectorAll<HTMLElement>(CANDIDATES))) {
    if (el.closest('[data-tour-skip]')) continue;
    const got = normalizeLabel(labelOf(el));
    if (!got) continue;
    if (got === want) {
      if (isVisible(el)) return el;
      continue;
    }
    if (!partial && got.length <= want.length * 3 && got.includes(want) && isVisible(el)) {
      partial = el;
    }
  }
  if (partial) return partial;
  // A tile or list row whose label is one line inside a longer control
  // ("File Upload CSV/XLSX/JSON" above its description): match that line and
  // point at the control around it.
  for (const el of Array.from(root.querySelectorAll<HTMLElement>(TEXT_LINES))) {
    if (el.closest('[data-tour-skip]')) continue;
    if (el.children.length > 0) continue;
    if (normalizeLabel(el.textContent || '') !== want) continue;
    const host = el.closest<HTMLElement>(CANDIDATES) ?? el;
    if (isVisible(host)) return host;
  }
  return null;
}
